import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { PerformanceTimingManager } from '@databases/frontend-metrics/performance-timing-manager';
import { defaultStoreUtils } from '@databases/redux';
import { doAddEntry } from '@databases/redux/table-layout/actions';
import { getService } from '@databases/service-container';
import { type DatabaseEntry } from '@databases/types/database';
import useView from '@databases/views/useView';
import { useCallback } from 'react';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

/** Returns a function to handle adding a new entry to the database. */
const useAddEntry = (contentId: string, onAdd?: (entry: DatabaseEntry) => void) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const client = useAnalytics();
  const {
    view: { settings: viewSettings },
  } = useView();

  return useCallback(() => {
    const reliability = ReliabilityMetrics.getInstance();
    try {
      const clickTime = performance.now();
      selectionsActions.deselectAll();
      client.sendAddEntryButtonClickedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'tableLayout',
      });
      const entry = dispatch(doAddEntry(contentId, viewSettings));
      PerformanceTimingManager.startMetricTiming({
        metricName: MetricName.DATABASE_ADD_ENTRY_TIME,
        id: entry.id,
        startTime: clickTime,
      });
      onAdd?.(entry);
      reliability.success(MetricName.DATABASE_ADD_ENTRY);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      reliability.failure(MetricName.DATABASE_ADD_ENTRY, { errorMessage });
      throw e;
    }
  }, [client, contentId, dispatch, onAdd, selectionsActions, viewSettings]);
};

export default useAddEntry;
